import parse from "html-react-parser";
import { isEmptyObject } from "jquery";
import * as _ from "lodash";
import * as React from "react";
import { useEffect, useState } from "react";
import { encodeXML } from "../../classes/XmlHelper";
import { IConfig } from "../../Config";
import { ViewType } from "../../NewsroomView";
import ArchiveButton from "../RenderComponents/ArchiveButton";
import DeleteButton from "../RenderComponents/DeleteButton";
import EditButton from "../RenderComponents/EditButton";
import RssFeedButton from "../RenderComponents/RssFeedButton";
import BackButton from "./components/BackButton";
import Filter from "./components/Filter";
import NextButton from "./components/NextButton";
import PreviousButton from "./components/PreviousButton";
import RecentPosts from "./components/RecentPosts";
import ShareButton from "./components/ShareButton";

interface IProps {
    settings: any;
    recentNewsPosts: any[];
    isAdmin: boolean;
    isViewingArchivedItems: boolean;
    post: any;
    config: IConfig;
    template: string;
    stylesheet: string;
    categories: any[];
    tags: any[];
    feed: any;
    showAuthor: boolean;
    hasPrev: boolean;
    hasNext: boolean;
    title: (post, cssClass?, noAnchor?) => void;
    getRecentPostUrl: (newsId: string) => string;
    onReadRecentPost: (newsId: string) => void;
    onBack: () => void;
    onDelete: (newsId: string, isDeleted: boolean, isPermanent: boolean) => void;
    onPrevPost: () => void;
    onNextPost: () => void;
    onSwitchView: (view: ViewType, newsId: string) => void;
}

const DefaultRecentNewsCount = 5;

const Template = (props: IProps) => {
    const [html, setHtml] = useState("");

    useEffect(() => {
        try {
            if (_.isEmpty(props.template)) {
                return;
            }
            const publishDate = new Date(Date.parse(props.post?.scheduledDate?.replace(/['"]+/g, "")));
            publishDate.setDate(publishDate.getDate() + 1);
            publishDate.setHours(0, 0, 0, 0);
            const readMode = props.isViewingArchivedItems ? "archived" : "news";
            const pathname = !isEmptyObject(props.post?.newsId) ? `#/${props.config.portletInstanceId}/read/${readMode}/${props.post.newsId}` : "";
            const xmlString = props.post ? `
                <post>
                    <title><![CDATA[${props.post?.title || ""}]]></title>
                    <summary><![CDATA[${props.post?.summary || ""}]]></summary>
                    <body><![CDATA[${props.post?.body || ""}]]></body>
                    <news_id>${props.post?.newsId || ""}</news_id>
                    <news_friendlyurl>${props.post?.friendlyUrlName}</news_friendlyurl>
                    <featured_image>${encodeXML(props.post?.featuredImage)}</featured_image>
                    <image_title>${encodeXML(props.post?.featuredImageCaption)}</image_title>
                    <published_date>${encodeXML(props.post?.scheduledDate)}</published_date>
                    <show_author>${props?.showAuthor}</show_author>
                    <show_recent_posts>${props.settings.ShowRecentPosts}</show_recent_posts>
                    <author>${encodeXML(props.post?.author)}</author>
                    <link_of_current_page>${encodeXML(pathname || "")}</link_of_current_page>
                    <show_rss>true</show_rss>
                    <rss_url>${encodeXML(props.config.rssUrl || "")}</rss_url>
                </post>
            ` : "<post></post>";
            const parser = new DOMParser();
            const xml = parser.parseFromString(xmlString, "text/xml");
            const xsl = parser.parseFromString(props.template, "text/xml");
            const xsltProcessor = new XSLTProcessor();
            xsltProcessor.importStylesheet(xsl);
            const resultDocument = xsltProcessor.transformToFragment(xml, document);
            const tempNode = document.createElement("div");
            tempNode.appendChild(resultDocument);
            setHtml(tempNode.outerHTML);
        } catch (ex) {
            console.error(ex);
        }
    }, [props.post, props.template, props.showAuthor]);

    useEffect(() => {
        if (_.isEmpty(props.stylesheet)) {
            return;
        }

        const head = document.head;
        const link = document.createElement("style");
        link.innerHTML = props.stylesheet;
        head.appendChild(link);

        return () => { head.removeChild(link); };
    }, [props.stylesheet]);

    const getAttributeValue = (domNode, attrName, optional = false) => {
        const attr = (((domNode as any)?.attribs || {})[attrName] || "").trim();
        if (!optional && attr === "") {
            const name = ((domNode as any).name || "").toLowerCase();
            const error = `${name} is missing the "${attrName}" attribute`;
            console.error(error);
            throw new Error(error);
        }
        return attr;
    };

    return (
        <>
            {parse(html, {
                replace: (domNode) => {
                    try {
                        const name = ((domNode as any).name || "").toLowerCase();
                        switch (name) {
                            case "archivebutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const archivedTitle = getAttributeValue(domNode, "archived-title", true);
                                const unarchivedTitle = getAttributeValue(domNode, "unarchived-title", true);
                                const archivedAriaLabel = getAttributeValue(domNode, "archived-aria-label", true);
                                const unarchivedAriaLabel = getAttributeValue(domNode, "unarchived-aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <ArchiveButton
                                        isAdmin={props.isAdmin}
                                        post={props.post}
                                        onDelete={props.onDelete}
                                        className={className}
                                        type={type}
                                        text={text}
                                        archivedTitle={archivedTitle}
                                        unarchivedTitle={unarchivedTitle}
                                        archivedAriaLabel={archivedAriaLabel}
                                        unarchivedAriaLabel={unarchivedAriaLabel}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "backbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <BackButton
                                        className={className}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        showIcon={showIcon}
                                        onBack={props.onBack}
                                    />
                                );
                            }
                            case "deletebutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <DeleteButton
                                        isAdmin={props.isAdmin}
                                        post={props.post}
                                        onDelete={props.onDelete}
                                        className={className}
                                        type={type}
                                        text={text}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "editbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <EditButton
                                        isAdmin={props.isAdmin}
                                        config={props.config}
                                        post={props.post}
                                        className={className}
                                        type={type}
                                        text={text}
                                        showIcon={showIcon}
                                        onSwitchView={props.onSwitchView}
                                    />
                                );
                            }
                            case "filter": {
                                return (
                                    <Filter settings={props.settings} categories={props.categories} tags={props.tags} feed={props.feed} />
                                );
                            }
                            case "nextbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <NextButton
                                        hasNext={props.hasNext}
                                        onNextPost={props.onNextPost}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "previousbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <PreviousButton
                                        hasPrev={props.hasPrev}
                                        onPrevPost={props.onPrevPost}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "recentposts": {
                                const buttonClassName = getAttributeValue(domNode, "button-class", true);
                                const buttonType = getAttributeValue(domNode, "button-type", true);
                                const buttonText = getAttributeValue(domNode, "button-text", true);
                                const buttonTitle = getAttributeValue(domNode, "button-title", true);
                                const buttonAriaLabel = getAttributeValue(domNode, "button-aria-label", true);
                                const maxArticles = getAttributeValue(domNode, "max-articles", true);
                                let recentNewsCount = DefaultRecentNewsCount;
                                try {
                                    const count = parseInt(maxArticles, 10);
                                    recentNewsCount = isNaN(count) ? DefaultRecentNewsCount : Math.max(count, 0);
                                } catch (ex) {
                                    console.error(ex);
                                }
                                const recentNews = props.recentNewsPosts
                                    .filter((n) => n.newsId !== props.post.newsId)
                                    .slice(0, recentNewsCount);
                                return (
                                    <RecentPosts
                                        config={props.config}
                                        settings={props.settings}
                                        recentNews={recentNews}
                                        getRecentPostUrl={props.getRecentPostUrl}
                                        onReadRecentPost={props.onReadRecentPost}
                                        buttonClassName={buttonClassName}
                                        buttonType={buttonType}
                                        buttonText={buttonText}
                                        buttonTitle={buttonTitle}
                                        buttonAriaLabel={buttonAriaLabel}
                                    />
                                );
                            }
                            case "rssfeedbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <RssFeedButton
                                        config={props.config}
                                        className={className}
                                        type={type}
                                        text={text}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "sharebutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <ShareButton
                                        className={className}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            default:
                                return domNode;
                        }
                    } catch (ex) {
                        return null;
                    }
                },
            })}
        </>
    );
};

export default Template;
